// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useRequestMint2Step } from '@hooks/mutations/registry/mint/useRequestMint2Step';
import { useCallback, useState } from 'react';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { InstrumentSelections } from '../../../components/InstrumentSelections';
import { useParties } from '../../../contexts/PartiesContext';

type DialogProps = {
  open: boolean;
  onClose: () => void;
};

const isPositiveNumber = (value: string) => {
  if (Number.isNaN(parseFloat(value))) {
    return 'Ensure value is a number';
  }
  if (parseFloat(value) < 0) {
    return 'Ensure value is positive';
  }
  return '';
};

export const RequestMintDialog = ({ open, onClose }: DialogProps) => {
  const [instrumentId, setInstrumentId] = useState<string>('');
  const { party } = useParties();
  const [amount, setAmount] = useState<string>('');
  const [label, setLabel] = useState<string>('');
  const [registrar, setRegistrar] = useState<string>('');
  const [reference, setReference] = useState<string>('');
  const [amountValidationMsg, setAmountValidationMsg] = useState<string>('');
  const requestMintTwoStep = useRequestMint2Step();
  const resetState = useCallback(() => {
    setInstrumentId('');
    setAmount('');
    setLabel('');
    setRegistrar('');
    setReference('');
  }, [setInstrumentId, setAmount, setLabel, setRegistrar, setReference]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);
  const twoStepMintRequest = async () => {
    await requestMintTwoStep({
      party,
      amount,
      source: registrar,
      instrumentId,
      reference,
    });
    handleClose();
  };

  const onInstrumentChange = (instId: string, instRegistrar: string) => {
    setInstrumentId(instId);
    setRegistrar(instRegistrar);
  };

  const disableSubmit = !instrumentId || !amount || !registrar;
  return (
    <FormDialog
      open={open}
      title="Request Mint"
      onCancel={handleClose}
      onSubmit={twoStepMintRequest}
      submitName="Request"
      isSubmitDisabled={disableSubmit}
    >
      <InstrumentSelections
        instrumentId={instrumentId}
        registrar={registrar}
        onInstrumentChange={onInstrumentChange}
      />
      <TextInput
        setValidationMessage={setAmountValidationMsg}
        getValidationMessage={isPositiveNumber}
        validationMessage={amountValidationMsg}
        label="Amount"
        value={amount}
        setValue={setAmount}
        required
      />
      <TextInput label="Label" value={label} setValue={setLabel} hidden />
      <TextInput label="Reference" value={reference} setValue={setReference} />
    </FormDialog>
  );
};
